// web/src/components/ui/Stepper.jsx
// A number you change by tapping, not by typing.
//
// Taylor, on correcting a count: "if we could have a quick add/subtract button
// that would be game changer." She is right, and the reason is the setting —
// this gets used standing at a shelf, on a phone, counting bottles. Typing
// means a keyboard sliding up over the thing you were reading, and a numeric
// keypad that covers the Save button.
//
// The field stays typable, because twelve taps to enter 12 is its own kind of
// silly. It is just no longer the only way in.
//
// `value` is a string and `onChange` must be a React state setter: the buttons
// hand it an updater so rapid taps accumulate instead of collapsing.
//
// 48px targets. The same floor the groom's checklist uses, for the same
// reason: gloves, winter, one hand holding something else.

export default function Stepper({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  disabled = false,
  label,
  id,
}) {
  // The value travels as a string so a half-typed field isn't clobbered, and
  // only the buttons round it to something sane.
  const n = Number(value);
  const known = Number.isFinite(n);

  // Each tap counts from what the LAST tap left, not from what this render
  // happened to be given. React batches updates inside one tick, so three
  // quick taps on a plain `onChange(value + 1)` all read the same stale
  // number and move it by one — measured, not theorised: tapping + three
  // times went 1 to 2. `onChange` therefore has to be a state setter, which
  // is what both callers pass.
  const by = (delta) =>
    onChange((prev) => {
      const p = Number(prev);
      const base = Number.isFinite(p) ? Math.round(p) : min;
      return String(Math.min(max, Math.max(min, base + delta)));
    });

  return (
    <div className="flex items-stretch gap-2">
      <button
        type="button"
        onClick={() => by(-step)}
        disabled={disabled || (known && n <= min)}
        aria-label={`One fewer${label ? ` — ${label}` : ''}`}
        className="w-12 h-12 shrink-0 rounded-lg border border-ink-200 bg-white text-2xl leading-none
                   text-ink-700 hover:bg-ink-50 active:bg-ink-100
                   disabled:text-ink-300 disabled:bg-ink-50"
      >
        −
      </button>

      <input
        id={id}
        className="input w-20 h-12 text-center text-xl tabular-nums font-medium"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => {
          // Digits only. A stray letter here becomes NaN and then a count of
          // zero, which on this screen means somebody's shelf is emptied.
          const t = e.target.value.replace(/[^0-9]/g, '');
          onChange(t);
        }}
        onFocus={(e) => e.target.select()}
        onBlur={() => { if (!known) onChange(String(min)); }}
      />

      <button
        type="button"
        onClick={() => by(step)}
        disabled={disabled || (known && n >= max)}
        aria-label={`One more${label ? ` — ${label}` : ''}`}
        className="w-12 h-12 shrink-0 rounded-lg border border-ink-200 bg-white text-2xl leading-none
                   text-ink-700 hover:bg-ink-50 active:bg-ink-100
                   disabled:text-ink-300 disabled:bg-ink-50"
      >
        +
      </button>
    </div>
  );
}
